import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import React, { useEffect, useMemo, useState } from 'react';
import { getSearchBox } from '../../lib/utils/coveo/searchBoxes';
import SearchFormInput from '../HeroAndSearch/SearchFormInput';
import {
  StCRGridContainerWithoutBackground,
  StCommonTypography,
} from '../shared/Container/Container.styles';
import { Text } from '@sitecore-jss/sitecore-jss-nextjs';
import { StInputBase } from '../shared/TextBox/TextBox.style';
import { UIButton } from '../shared/UIButton.styles';
import { RootState } from '../../store/store';
import { useSelector } from 'react-redux';
const fallbackField = {
  value: '',
};
const TrialSearch: React.FC<any> = (props) => {
  const theme = useTheme();
  // const router = useRouter();

  const [zipCode, setZipCode] = useState('');
  const [error, setError] = useState({ searchText: false, zipCode: false });
  const userProfile = useSelector((state: RootState) => state.crProfile);
  const profileDetails = userProfile?.profileData?.basicDetails;
  const searchTitle = props.fields?.searchTitle || fallbackField;
  const conditionLabel = props.fields?.conditionLabel || fallbackField;
  const conditionErrMsg = props.fields?.conditionRequiredValidationMsg.value || fallbackField;
  const zipcodeLabel = props.fields?.zipcodeLabel || fallbackField;
  const zipcodeErrMsg = props.fields?.zipcodeRegexErrorMessage || fallbackField;
  const zipcodeRegexString = props.fields?.zipcodeRegex?.fields?.value?.value || '';
  const isZipCodeRequired = props.fields?.isZipCodeRequired?.value || false;
  const lengthToSuggestion = props.fields?.lengthToSuggestion?.value || 3;
  const searchLink = props?.fields?.searchLink || {};
  const searchLinkText = searchLink?.value?.text || '';
  // const searchLinkHref = searchLink?.value?.href || '';

  const searchBox = useMemo(
    () =>
      getSearchBox(
        props?.fields?.totalSuggestion?.value || 20,
        props?.fields?.searchlink?.value?.href || '/find-trials/search-result'
      ),
    []
  );

  useEffect(() => {
    if (typeof window !== 'undefined') {
      searchBox.subscribe(() => {
        const { redirectTo, value, analytics } = searchBox.state;

        if (redirectTo) {
          const data = { value, analytics };
          localStorage.setItem('coveo_standalone_search_box_data', JSON.stringify(data));
          searchBox.afterRedirection();
          window.location.href = redirectTo;
        }
      });
    }
  }, []);

  useEffect(() => {
    if (profileDetails?.zipCode && !zipCode) {
      localStorage.setItem('zipCode', profileDetails.zipCode);
    } else {
      localStorage.setItem('zipCode', zipCode);
    }
  }, [profileDetails?.zipCode, zipCode]);

  const onZipCodeChange = (event: any) => {
    const value = event.target?.value;
    setZipCode(value);
    if (isZipCodeRequired && typeof zipcodeRegexString === 'string') {
      const regexToCheck = new RegExp(zipcodeRegexString);
      setError((obj) => {
        return { ...obj, zipCode: !regexToCheck.test(value) };
      });
    }
  };

  const onSearchTextChange = () => {
    const searchValue = searchBox?.state?.value || '';
    setError((obj) => {
      return { ...obj, searchText: !Boolean(searchValue) };
    });
  };

  const coveoSearchSubmit = () => {
    const searchText = searchBox?.state?.value;
    let isValid = true; // Variable to track overall validity
    if (!searchText) {
      setError((obj) => {
        return { ...obj, searchText: true };
      });
      isValid = false;
    }
    if (isZipCodeRequired && typeof zipcodeRegexString === 'string') {
      const regexToCheck = new RegExp(zipcodeRegexString);
      if (!regexToCheck.test(zipCode)) {
        setError((obj) => {
          return { ...obj, zipCode: true };
        });
        isValid = false;
      }
    }
    if (isValid) {
      searchBox.submit();
      // if (searchLinkHref) {
      //   router.push(searchLinkHref);
      // }
    }
  };

  if (!props?.fields) return <></>;
  return (
    <Box
      paddingY={'1rem'}
      sx={{
        background: 'linear-gradient(1.33deg, #005587 -2.85%, #00BEB1 144.31%)',
      }}
    >
      <StCRGridContainerWithoutBackground
        sx={{
          [theme.breakpoints.down('lg')]: {
            marginX: '4rem',
            textAlign: 'center',
          },
          [theme.breakpoints.down('md')]: {
            marginX: '2rem',
          },
        }}
      >
        <StCommonTypography
          color={theme.palette.neutral0.main}
          sx={{
            marginBottom: '1rem',
            fontSize: '1.5rem',
            lineHeight: '1.5rem',
          }}
        >
          <Text field={searchTitle} />
        </StCommonTypography>
        <form>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              columnGap: '2rem',
              [theme.breakpoints.down('lg')]: {
                width: '100%',
                flexDirection: 'column',
                rowGap: '1rem',
                justifyContent: 'center',
                alignItems: 'center',
              },
            }}
          >
            <Box
              sx={{
                flex: 2,
                [theme.breakpoints.down('lg')]: {
                  width: '100%',
                },
              }}
            >
              <SearchFormInput
                searchBox={searchBox}
                onChange={onSearchTextChange}
                value={''}
                fieldID="trial-search-coi"
                lengthToSuggestion={lengthToSuggestion}
                placeholder={conditionLabel.value}
                style={{
                  background: theme.palette.white.main,
                  padding: '0px',
                  borderRadius: '8px',
                  fontFamily: 'Proxima Nova',
                  fontSize: '14px',
                  lineHeight: '24px',
                  fontWeight: 400,
                  '& .MuiOutlinedInputNotchedOutline': {
                    borderColor: error.searchText ? `${theme.palette.error.main}` : 'inherit',
                  },
                }}
              />
              {error.searchText && (
                <Box
                  sx={{
                    textAlign: 'left',
                    [theme.breakpoints.up('lg')]: { position: 'absolute' },
                  }}
                >
                  <StCommonTypography variant="body2" color="error">
                    {conditionErrMsg}
                  </StCommonTypography>
                </Box>
              )}
            </Box>

            <Box
              sx={{
                flex: 2,
                [theme.breakpoints.down('lg')]: {
                  width: '100%',
                },
              }}
            >
              <StInputBase
                value={zipCode}
                placeholder={zipcodeLabel.value}
                onChange={onZipCodeChange}
                error={error.zipCode}
                sx={{
                  borderRadius: '8px',
                  width: '100%',
                  fontFamily: 'Proxima Nova',
                  '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.error.main,
                  },
                  '& input::placeholder': {
                    color: `${theme.palette.neutral11.main} !important`,
                    opacity: 1,
                    fontSize: '13px',
                    lineHeight: '16px',
                  },
                }}
              />
              {error.zipCode && (
                <Box
                  sx={{
                    textAlign: 'left',
                    [theme.breakpoints.up('lg')]: { position: 'absolute' },
                  }}
                >
                  <StCommonTypography variant="body2" color="error">
                    {zipcodeErrMsg.value}
                  </StCommonTypography>
                </Box>
              )}
            </Box>

            <Box
              sx={{
                flex: 1,
                [theme.breakpoints.down('lg') && theme.breakpoints.up('md')]: {
                  width: '50%',
                },
                [theme.breakpoints.down('md')]: {
                  width: '100%',
                },
              }}
            >
              <UIButton
                variant={'outlined'}
                onClick={coveoSearchSubmit}
                sx={{
                  minHeight: '40px',
                  [theme.breakpoints.down('md')]: {
                    padding: '6px',
                  },
                  [theme.breakpoints.down(530)]: {
                    width: '100%',
                    padding: '6px',
                  },
                }}
              >
                {searchLinkText}
              </UIButton>
            </Box>
          </Box>
        </form>
      </StCRGridContainerWithoutBackground>
    </Box>
  );
};

export default TrialSearch;
